import { zodResolver } from '@hookform/resolvers/zod';
import { getPlainTextLengthFromHtml } from '@talentmesh/rte';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCreateRecruitmentContext } from '../../Create/Contexts/CreateRecruitmentContext';
import { JobAd } from '../../Models/JobAd';
import StepProps from '../StepProps';
import FailedToGenerateJobAdDialog from './Components/FailedToGenerateJobAdDialog';
import JobAdCompanyDescription from './Components/JobAdCompanyDescription';
import JobAdExplanation from './Components/JobAdExplanation';
import JobAdStepHeader from './Components/JobAdStepHeader';
import JobAdStepNavigation from './Components/JobAdStepNavigation';
import JobAdTitle from './Components/JobAdTitle';
import JobDescription from './Components/JobDescription';
import RequiredSkills from './Components/RequiredSkills';
import { jobAdSchema, type JobAdFormValues } from './JobAdStep.schema';
import { jobAdFormValues2Model, jobAdModel2FormValues } from './JobAdStepUtils';
import ProcessingState from '@/Models/ProcessingState';
import UIStrings from '@/Utils/UIStrings';

const CreateJobAdStep = ({ activeStep, handleNext, handleBack }: StepProps) => {
    const {
        jobDetails,
        jobAd,
        setJobAd,
        generateJobAdAsync,
        regenerateAtStep2,
        regenerateExperienceCriteria,
        showErrorDialog,
        closeDialog,
        generateExperienceCriteria,
        processingState,
        setShouldRegenExperienceCriteria,
        shouldGenerateJobAdCompanyDescription,
        isExperienceCriteriaPrefilled,
    } = useCreateRecruitmentContext();
    const [isJobAdGenerated, setIsJobAdGenerated] = useState(false);
    // https://github.com/orgs/react-hook-form/discussions/3700 https://codesandbox.io/p/sandbox/reack-hook-form-initial-validation-forked-9zmqt?file=%2Fsrc%2FApp.tsx%3A42%2C20
    const [isReseted, setIsReset] = useState(false);
    const shouldGenerateCompanyDescription = shouldGenerateJobAdCompanyDescription();
    const form = useForm({
        mode: 'all',
        resolver: zodResolver(jobAdSchema),
        defaultValues: jobAdModel2FormValues(jobAd),
    });

    // formState object is not the latest when used directly
    const getIsFormDirty = useCallback(() => form.formState.isDirty, [form.formState.isDirty]);

    const saveJobAd = (newJobAd: JobAd) => {
        setJobAd(newJobAd);
        setShouldRegenExperienceCriteria(isJobAdGenerated || getIsFormDirty());
    };

    const submitHandlerAsync = async (values: JobAdFormValues) => {
        const newJobAd = jobAdFormValues2Model(values);
        setJobAd(newJobAd);

        if (!isExperienceCriteriaPrefilled && (isJobAdGenerated || regenerateExperienceCriteria || getIsFormDirty())) {
            generateExperienceCriteria(newJobAd);
        }

        if (handleNext) {
            handleNext();
        }
    };

    const handleBackWrapper = () => {
        const values = form.getValues();
        saveJobAd(jobAdFormValues2Model(values));

        if (handleBack) {
            handleBack();
        }
    };

    useEffect(() => {
        if (processingState === ProcessingState.Success) {
            const descriptionLen = getPlainTextLengthFromHtml(jobAd.description);

            form.resetField('title', { defaultValue: jobAd.title, keepTouched: true, keepDirty: true });
            form.resetField('description', { defaultValue: jobAd.description, keepTouched: true, keepDirty: true });
            form.resetField('descriptionLen', { defaultValue: descriptionLen, keepTouched: true, keepDirty: true });
            form.resetField('companyDescription', {
                defaultValue: jobAd.companyDescription,
                keepTouched: true,
                keepDirty: true,
            });

            setIsReset(true);
        }
    }, [processingState]);

    useEffect(() => {
        if (isReseted) {
            void form.trigger();
        }
    }, [isReseted]);

    useEffect(() => {
        const doGenerateJobAd = async () => {
            if (regenerateAtStep2) {
                await generateJobAdAsync();
                setIsJobAdGenerated(true);
            }
        };

        doGenerateJobAd();
    }, []);

    return (
        <div className="mx-auto w-full max-w-4xl self-center">
            <JobAdStepHeader />
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(submitHandlerAsync)}>
                    <div className="border-border bg-alicia flex flex-col gap-7 rounded-lg border px-5 py-8">
                        <JobAdExplanation />
                        <JobAdTitle loading={processingState === ProcessingState.Processing} />
                        <RequiredSkills />
                        <JobDescription initialHtml={jobAd.description} processingState={processingState} />
                        <JobAdCompanyDescription
                            initialHtml={jobAd.companyDescription}
                            processingState={processingState}
                            showLoadingOverlay={shouldGenerateCompanyDescription}
                        />
                    </div>
                    <JobAdStepNavigation
                        activeStep={activeStep}
                        handleBack={handleBackWrapper}
                        processingState={processingState}
                        jobAd={jobAd}
                        setJobAd={saveJobAd}
                        jobDetails={jobDetails}
                        nextButtonLabel={UIStrings.Next}
                    />
                </form>
            </FormProvider>
            <FailedToGenerateJobAdDialog
                open={showErrorDialog === 'JobAdGenerationError'}
                dialogTitle={UIStrings.JobAdGenerationErrorTitle}
                message1={UIStrings.JobAdGenerationErrorMessage}
                close={closeDialog}
            />
            <FailedToGenerateJobAdDialog
                open={showErrorDialog === 'TooManyRequests'}
                dialogTitle={UIStrings.JobAdGenerationErrorTitle}
                message1={UIStrings.JobAdGenerationLimitError}
                close={closeDialog}
            />
        </div>
    );
};

export default CreateJobAdStep;
